import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, CheckCircle2, Loader2, Newspaper, ArrowRight, Check, CheckCheck } from "lucide-react";
import { sr } from "../../lib/sr";
import { timeAgo } from "../../lib/timeAgo";

const TAG = {
  Feature: { cls: "sr-badge-blue", label: "New feature" },
  Improvement: { cls: "sr-badge-gold", label: "Improvement" },
  Fix: { cls: "sr-badge-green", label: "Fix" },
};

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

function broadcast(unread) {
  window.dispatchEvent(new CustomEvent("sr-updates-changed", { detail: { unread } }));
}

export default function WhatsNewApp() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);

  useEffect(() => {
    // Load with per-item read flags. We do NOT auto-clear everything here —
    // the user dismisses items one at a time or taps "Mark all read".
    sr.getUpdates().then((r) => setItems(r.updates || [])).catch(() => setItems([]));
  }, []);

  const unread = (items || []).filter((u) => !u.read).length;

  const dismissOne = (id) => {
    setItems((list) => list.map((u) => (u.id === id ? { ...u, read: true } : u)));
    sr.dismissUpdate(id).then((r) => broadcast(r.unread_count)).catch(() => {});
  };
  const markAll = () => {
    setItems((list) => list.map((u) => ({ ...u, read: true })));
    sr.markUpdatesSeen().then(() => broadcast(0)).catch(() => {});
  };
  const goTo = (u) => {
    if (!u.read) dismissOne(u.id);
    if (u.link?.web) nav(u.link.web);
  };

  return (
    <div data-testid="whats-new-app" style={{ maxWidth: 760 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <div className="sr-eyebrow" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Newspaper size={13} /> Product updates</div>
          <h1 style={{ fontSize: 32, marginTop: 6 }}>What's New</h1>
          <p className="sr-muted" style={{ marginTop: 6, fontSize: 14 }}>Every improvement, new feature and fix we ship, newest first.</p>
        </div>
        {unread > 0 && (
          <button type="button" className="sr-btn sr-btn-ghost sr-btn-sm" data-testid="whats-new-mark-all" onClick={markAll} style={{ marginTop: 28 }}>
            <CheckCheck size={14} /> Mark all read ({unread})
          </button>
        )}
      </div>

      {!items && (
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}><Loader2 size={22} className="sr-spin" style={{ color: "var(--blue)" }} /></div>
      )}

      {items && items.length === 0 && (
        <div className="sr-card" data-testid="whats-new-empty" style={{ marginTop: 20, textAlign: "center", padding: 40 }}>
          <Sparkles size={26} style={{ color: "var(--blue)" }} />
          <p className="sr-muted" style={{ marginTop: 10 }}>No updates yet — check back soon.</p>
        </div>
      )}

      <div style={{ display: "grid", gap: 14, marginTop: 20 }}>
        {(items || []).map((u) => {
          const tag = TAG[u.tag] || { cls: "sr-badge-gray", label: u.tag || "Update" };
          return (
            <article key={u.id || u.seq} className="sr-card" data-testid={`whats-new-item-${u.seq}`}
              style={{ padding: 20, position: "relative", borderLeft: u.read ? "1px solid var(--border)" : "3px solid var(--blue)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span className={`sr-badge ${tag.cls}`} style={{ fontSize: 11 }}>{tag.label}</span>
                {u.audience === "internal" && <span data-testid={`whats-new-internal-${u.seq}`} className="sr-badge sr-badge-gray" style={{ fontSize: 10 }}>Internal only</span>}
                {!u.read && <span data-testid={`whats-new-new-${u.seq}`} className="sr-badge sr-badge-red" style={{ fontSize: 10 }}>New</span>}
                <span className="sr-muted" style={{ fontSize: 12 }} title={fmtDate(u.date)}>{timeAgo(u.date)}</span>
              </div>
              <h3 style={{ fontSize: 19, marginTop: 10 }}>{u.title}</h3>
              {u.summary && <p className="sr-muted" style={{ fontSize: 14, marginTop: 6, lineHeight: 1.6 }}>{u.summary}</p>}
              {(u.highlights || []).length > 0 && (
                <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0", display: "grid", gap: 8 }}>
                  {u.highlights.map((h, i) => (
                    <li key={i} style={{ display: "flex", gap: 8, fontSize: 13.5, color: "var(--ink)", lineHeight: 1.5 }}>
                      <CheckCircle2 size={15} style={{ color: "var(--blue)", flexShrink: 0, marginTop: 2 }} /> <span>{h}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
                {u.link?.web && (
                  <button type="button" className="sr-btn sr-btn-primary sr-btn-sm" data-testid={`whats-new-go-${u.seq}`} onClick={() => goTo(u)}>
                    {u.link.label || "Open"} <ArrowRight size={14} />
                  </button>
                )}
                {!u.read && (
                  <button type="button" className="sr-btn sr-btn-ghost sr-btn-sm" data-testid={`whats-new-dismiss-${u.seq}`} onClick={() => dismissOne(u.id)}>
                    <Check size={14} /> Mark read
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
